import React, { useMemo, useState } from "react";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Icon, Spinner, Text } from "@performyard/py-components";

import AccordionSectionHeader from "./AccordionSectionHeader";
import PastMeetingItem from "./PastMeetingItem";

import { useMeetingsSearch } from "../../../../api/hooks";

import { config } from "../config";
import { useMeetingContext } from "../context";

type PastMeetingNotesAccordionProps = {
	workspaceId: string;
	currentMeetingStartDate: Date;
	currentMeetingId: string;
};

/**
 * Accordion showing past meeting notes with the ability to view/edit notes from previous meetings.
 */
const PastMeetingNotesAccordion = ({
	workspaceId,
	currentMeetingStartDate,
	currentMeetingId,
}: PastMeetingNotesAccordionProps): React.ReactElement | null => {
	const { userId } = useMeetingContext();
	const [isExpanded, setIsExpanded] = useState(false);
	const [expandedMeetingId, setExpandedMeetingId] = useState<string | null>(null);

	const { data: pastMeetingsData, isLoading: isLoadingPastMeetings } = useMeetingsSearch(
		{
			meetingWorkspaceId: workspaceId,
			dateRangeEnd: new Date(currentMeetingStartDate),
			sort: { name: "startDateTime", direction: "desc" },
			limit: config.pastMeetingsLimit + 1,
		},
		{ enabled: Boolean(workspaceId) && Boolean(currentMeetingStartDate) }
	);

	const pastMeetings = useMemo(() => {
		return pastMeetingsData?.filter((m) => m._id !== currentMeetingId).slice(0, config.pastMeetingsLimit) || [];
	}, [pastMeetingsData, currentMeetingId]);

	const handleToggle = (): void => {
		setIsExpanded(!isExpanded);
	};

	const handleMeetingToggle = (meetingId: string): void => {
		setExpandedMeetingId(expandedMeetingId === meetingId ? null : meetingId);
	};

	return (
		<div className="flex flex-col border-b border-solid border-neutral-2 bg-white">
			<AccordionSectionHeader title="Past Meeting Notes" isExpanded={isExpanded} onToggle={handleToggle} />

			{isExpanded && (
				<div
					className="flex flex-col overflow-y-auto bg-white"
					style={{ maxHeight: config.layout.accordionMaxHeight }}
				>
					{isLoadingPastMeetings ? (
						<div className="flex items-center justify-center py-4">
							<Spinner size={config.spinnerSize.small} />
						</div>
					) : pastMeetings.length === 0 ? (
						<div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center">
							<div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-1">
								<Icon icon={faMagnifyingGlass} size={config.iconSize.large} color="neutral-7" />
							</div>
							<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
								No Past Meeting Notes
							</Text>
						</div>
					) : (
						pastMeetings.map((pastMeeting) => (
							<PastMeetingItem
								key={pastMeeting._id}
								meetingId={pastMeeting._id}
								meetingName={pastMeeting.name}
								startDateTime={pastMeeting.startDateTime}
								endDateTime={pastMeeting.endDateTime}
								participants={pastMeeting.participants}
								isExpanded={expandedMeetingId === pastMeeting._id}
								onToggle={() => handleMeetingToggle(pastMeeting._id)}
								userId={userId}
							/>
						))
					)}
				</div>
			)}
		</div>
	);
};

export default PastMeetingNotesAccordion;
